import axios from 'axios'

/**
 * Shared, client-safe half of the "email already on another account" check.
 * The server half (`emailOwnership.ts`) imports the DB and firebase-admin, so
 * nothing a component imports may live there.
 */

/** How the OTHER account that already holds an email signs in. */
export type EmailOwnerProvider = 'google' | 'apple' | 'phone' | 'unknown'

/** Body of the 409 the email-writing routes answer with. */
export interface EmailInUseResponse {
  error: 'email_in_use'
  provider: EmailOwnerProvider
}

const PROVIDERS: readonly EmailOwnerProvider[] = [
  'google',
  'apple',
  'phone',
  'unknown',
]

/**
 * The owning account's provider when `error` is the routes' 409
 * `email_in_use` answer, otherwise `null`. An unrecognised provider value
 * becomes `unknown` rather than being trusted.
 */
export function readEmailInUse(error: unknown): EmailOwnerProvider | null {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) return null
  const data = error.response.data as Partial<EmailInUseResponse> | undefined
  if (data?.error !== 'email_in_use') return null
  return PROVIDERS.includes(data.provider as EmailOwnerProvider)
    ? (data.provider as EmailOwnerProvider)
    : 'unknown'
}

/** Shown when the one-tap join to a Google/Apple account doesn't go through,
 *  back on the input so the shopper is never stuck. */
export function joinFailedMessage(provider: 'google' | 'apple'): string {
  const label = provider === 'google' ? 'Google' : 'Apple'
  return `We couldn't link your accounts. Log in with ${label} to use this email, or enter a different email.`
}

/** What the shopper is told, by how the other account signs in. */
export function emailInUseMessage(provider: EmailOwnerProvider): string {
  switch (provider) {
    case 'google':
      return 'That email is already on a Dupe account you made with Google. Log in with Google to use it, or enter a different email.'
    case 'apple':
      return 'That email is already on a Dupe account you made with Apple. Log in with Apple to use it, or enter a different email.'
    case 'phone':
      return 'That email is already on another Dupe account. Log in with that phone number to use it, or enter a different email.'
    default:
      return 'That email is already on another Dupe account. Log in to that account to use it, or enter a different email.'
  }
}
